import { useState, useEffect, useCallback, useRef } from 'react';
import { isNativeApp } from '../utils/platform';
import { useAuth } from '../context/AuthContext';
import { isAppLockEnabled, verifyBiometric, getLockDelay, markUnlocked, getLastUnlock, NEVER_COLD_MS } from '../utils/biometric';
import { IcLock } from './Icons';

// Blocks the app behind a biometric prompt when the user has enabled App Lock
// (native only). Re-lock timing follows the user's chosen delay (Settings):
// 'immediately' … up to 2h of background, or 'never' (only re-prompt on a fresh
// launch after 6h). The verifying guard means a file/photo picker or the
// biometric prompt itself never re-locks mid-action. No-op on web / when off.

export default function BiometricGate({ children }) {
  const { currentUser } = useAuth();
  // Only lock a live session — never on the logged-out sign-in screen (that
  // screen has its own biometric button, which would otherwise double-prompt).
  const active = isNativeApp() && isAppLockEnabled() && !!currentUser;
  const [locked, setLocked] = useState(active);
  const [busy, setBusy] = useState(false);
  const hiddenAt = useRef(0);
  const verifying = useRef(false);
  const didMount = useRef(false);

  const unlock = useCallback(async () => {
    if (verifying.current) return;
    verifying.current = true;
    setBusy(true);
    const ok = await verifyBiometric('Unlock Toppers Tutorial');
    setBusy(false);
    verifying.current = false;
    if (ok) { markUnlocked(); setLocked(false); }
  }, []);

  // Cold-launch prompt. With 'never', only prompt when 6h have passed since the
  // last unlock; otherwise the launch is trusted and we skip straight in.
  useEffect(() => {
    if (!active) return;
    // Just verified elsewhere (e.g. biometric sign-in on the login screen, which
    // reloads into the app) — don't immediately prompt again.
    if (Date.now() - getLastUnlock() < 60000) { setLocked(false); return; }
    if (getLockDelay() === 'never') {
      if (Date.now() - getLastUnlock() >= NEVER_COLD_MS) unlock();
      else setLocked(false);
    } else {
      unlock();
    }
  }, []); // eslint-disable-line

  // Re-lock after the app has been in the background for the chosen delay.
  useEffect(() => {
    if (!active) return;
    const onVis = () => {
      const delay = getLockDelay();
      if (delay === 'never') return; // never re-lock on backgrounding
      const relockMs = parseInt(delay, 10) || 0;
      if (document.visibilityState === 'hidden') {
        if (!verifying.current) hiddenAt.current = Date.now();
      } else {
        const away = Date.now() - (hiddenAt.current || 0);
        if (!verifying.current && hiddenAt.current && away >= relockMs) {
          setLocked(true);
        }
        hiddenAt.current = 0;
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [active]);

  // Prompt again when we RE-lock after a background — but skip the initial mount
  // (the cold-launch effect already handles the first prompt; running both here
  // was the cause of the double biometric request).
  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return; }
    if (active && locked) unlock();
  }, [locked]); // eslint-disable-line

  if (!active || !locked) return children;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100000,
      background: 'var(--bg, #0f172a)', color: '#fff',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18, padding: 24,
    }}>
      <div style={{ width: 72, height: 72, borderRadius: 20, background: 'rgba(255,106,0,0.15)', border: '1px solid rgba(255,106,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <IcLock size={30} />
      </div>
      <div style={{ fontSize: 18, fontWeight: 800 }}>Toppers Tutorial</div>
      <div style={{ fontSize: 13, opacity: 0.6, textAlign: 'center', maxWidth: 260 }}>
        This app is locked. Verify with your fingerprint or face to continue.
      </div>
      <button className="btn btn-primary btn-md" onClick={unlock} disabled={busy} style={{ marginTop: 6 }}>
        {busy ? 'Verifying…' : 'Unlock'}
      </button>
    </div>
  );
}
